/**
 * A matemática do orbe da VecchIA, sem WebGL nem React (testada em `tests/unit/brain-orbe.test.ts`).
 *
 * O hook de voz entrega, a cada quadro, o nível e três bandas do som — de 0 a 1 — e de quem é
 * esse som. Aqui ele vira os números do shader em dois passos:
 *
 * 1. `suavizarSinal`: ataque rápido e soltura de ~400 ms, calculados sobre o tempo real do
 *    quadro — a mesma curva a 30 ou a 120 fps. É o que faz o orbe desacelerar sozinho na pausa
 *    entre duas frases, sem nada aleatório.
 * 2. `parametrosDoOrbe`: o que cada fase e cada banda mexem; `misturarParametros` passa de um
 *    conjunto ao outro sem salto quando a fase muda.
 *
 * Na esfera, graves movem as volutas grandes (torção larga) e deformam de leve a superfície,
 * médios fazem o fluido correr, agudos dão a ondulação fina da borda e a cintilação do reflexo.
 * Na aura — as ondas de vidro menta em volta, ouvindo e falando — graves abrem as ondas grandes,
 * médios as fazem fluir (o tempo corre mais rápido) e agudos dão o tremor fino do contorno.
 */

import type { FaseDaVoz } from '../useConversaPorVoz';

export type FonteDoSinal = 'microfone' | 'voz' | 'nenhuma';

/** Tudo de 0 a 1. `voz` = áudio REAL de saída da VecchIA; `microfone` = a pessoa; `nenhuma` = sem analisador. */
export interface SinalDaVoz {
    fonte: FonteDoSinal;
    nivel: number;
    graves: number;
    medios: number;
    agudos: number;
}

export type Energia = Omit<SinalDaVoz, 'fonte'>;

export const ENERGIA_ZERO: Energia = { nivel: 0, graves: 0, medios: 0, agudos: 0 };

/** O maior passo de tempo considerado: depois de uma aba escondida, o orbe não dá um salto. */
const PASSO_MAXIMO_MS = 250;

export function limitar01(valor: number): number {
    return Number.isFinite(valor) ? Math.min(1, Math.max(0, valor)) : 0;
}

/** Um passo de suavização exponencial: sobe com a constante `ataqueMs` e desce com `solturaMs`. */
export function suavizar(atual: number, alvo: number, dtMs: number, ataqueMs = 70, solturaMs = 400): number {
    const destino = limitar01(alvo);
    const tau = destino > atual ? ataqueMs : solturaMs;
    const passo = 1 - Math.exp(-Math.min(Math.max(dtMs, 0), PASSO_MAXIMO_MS) / tau);
    return atual + (destino - atual) * passo;
}

/** Graves são massas: sobem e descem mais devagar. Agudos são reflexos: rápidos. */
export function suavizarSinal(atual: Energia, sinal: Energia, dtMs: number): Energia {
    return {
        nivel: suavizar(atual.nivel, sinal.nivel, dtMs),
        graves: suavizar(atual.graves, sinal.graves, dtMs, 110, 480),
        medios: suavizar(atual.medios, sinal.medios, dtMs, 80, 400),
        agudos: suavizar(atual.agudos, sinal.agudos, dtMs, 35, 240),
    };
}

/**
 * A voz do navegador não passa por analisador: enquanto ela fala, um envelope sintético e
 * suave — sílabas perto de 4 Hz dentro de frases de uns 3 s. Determinístico, e só entra quando
 * não há áudio real.
 */
export function falaSintetica(tempoS: number): Energia {
    const frase = 0.5 + 0.5 * Math.sin(tempoS * Math.PI * 2 * 0.3);
    const silaba = 0.5 + 0.5 * Math.sin(tempoS * Math.PI * 2 * 3.7 + 1.4 * Math.sin(tempoS * 1.1));
    const nivel = limitar01(0.1 + 0.32 * frase * (0.4 + 0.6 * silaba));
    return { nivel, graves: limitar01(nivel * 0.85), medios: limitar01(nivel * 0.9), agudos: limitar01(nivel * 0.5 * silaba) };
}

export interface ParametrosDoOrbe {
    /** Velocidade do tempo do fluido e da aura (1 = o movimento base, muito lento). */
    velocidade: number;
    /** Amplitude da deformação da superfície da esfera (graves). */
    deformacao: number;
    /** Força da torção do fluido dentro da esfera (médios). */
    fluxo: number;
    /** 0 a 1: ondulação fina da borda, tremor do contorno da aura e cintilação do reflexo (agudos). */
    ondulacao: number;
    /** 0 a 1: energia geral — luz interna e halo mais espalhado. */
    energia: number;
    /** Multiplicador de luz: menor que 1 na pausa. */
    brilho: number;
    /** 0 a 1: o fluido corre para o centro e a aura se recolhe junto da esfera (pensando). */
    recolher: number;
    /** 0 a 1: presença da aura líquida fora da esfera — quase nada em repouso. */
    aura: number;
    /** 0 a 1: o quanto as ondas grandes da aura se abrem (graves). */
    abertura: number;
    /** 0 a 1: intensidade do halo menta em volta da esfera. */
    halo: number;
}

/** A deformação de repouso: a superfície nunca é um círculo perfeito, mas quase. */
const DEFORMACAO_BASE = 0.01;
/** O quanto os graves deformam a esfera no máximo: o movimento grande fica com a aura. */
const DEFORMACAO_DOS_GRAVES = 0.03;

export const PARAMETROS_EM_REPOUSO: ParametrosDoOrbe = {
    velocidade: 1, deformacao: DEFORMACAO_BASE, fluxo: 0.35, ondulacao: 0, energia: 0, brilho: 1, recolher: 0, aura: 0.05, abertura: 0, halo: 0.45,
};

/** O quanto o som pesa em cada fase: a VecchIA falando é quem mais mexe; a pessoa, um pouco menos. */
const GANHO_DA_FASE: Record<FaseDaVoz, number> = { speaking: 1, listening: 0.7, thinking: 0.25, idle: 0.25, paused: 0 };

/** Aura e halo de cada fase, antes do som: em repouso só o halo; ouvindo e falando a aura aparece. */
const PRESENCA_DA_FASE: Record<FaseDaVoz, { aura: number; halo: number }> = {
    idle: { aura: 0.05, halo: 0.45 },
    paused: { aura: 0.02, halo: 0.3 },
    listening: { aura: 0.5, halo: 0.75 },
    speaking: { aura: 0.6, halo: 0.8 },
    thinking: { aura: 0.3, halo: 0.6 },
};

/**
 * Os números do shader para a fase e o som já suavizado. Com áudio real (`voz` ou `microfone`),
 * o mesmo som dá sempre o mesmo desenho — nada aleatório; só a voz do navegador (`nenhuma`
 * falando) usa `falaSintetica`.
 */
export function parametrosDoOrbe(fase: FaseDaVoz, fonte: FonteDoSinal, energia: Energia, tempoS: number, reduzido = false): ParametrosDoOrbe {
    const som = fonte !== 'nenhuma' ? energia : fase === 'speaking' ? falaSintetica(tempoS) : ENERGIA_ZERO;
    const ganho = GANHO_DA_FASE[fase];
    const presenca = PRESENCA_DA_FASE[fase];
    const viva = fase === 'listening' || fase === 'speaking';
    const p: ParametrosDoOrbe = {
        velocidade: 1 + (som.nivel * 1.8 + som.medios * 1.1) * ganho,
        deformacao: DEFORMACAO_BASE + som.graves * DEFORMACAO_DOS_GRAVES * ganho,
        fluxo: 0.35 + som.medios * 0.95 * ganho,
        ondulacao: limitar01(som.agudos * ganho),
        energia: limitar01(som.nivel * ganho),
        brilho: 1,
        recolher: 0,
        aura: limitar01(presenca.aura + (viva ? som.nivel * 0.5 * ganho : 0)),
        abertura: viva ? limitar01(0.12 + som.graves * 0.88 * ganho) : 0,
        halo: limitar01(presenca.halo + som.nivel * 0.25 * ganho),
    };
    if (fase === 'thinking') {
        p.recolher = 1;
        p.velocidade = Math.max(p.velocidade, 1.7);
        p.fluxo += 0.12;
        p.brilho = 1.03;
    } else if (fase === 'paused') {
        p.velocidade = 0.5;
        p.brilho = 0.9;
    }
    if (reduzido) {
        p.velocidade = Math.max(0.12, p.velocidade * 0.3);
        p.deformacao = DEFORMACAO_BASE + (p.deformacao - DEFORMACAO_BASE) * 0.35;
        p.fluxo = 0.35 + (p.fluxo - 0.35) * 0.35;
        p.ondulacao *= 0.3;
        p.energia *= 0.5;
        p.abertura *= 0.4;
        p.aura *= 0.75;
    }
    return p;
}

/** Constantes de tempo, em ms: o que segue o som responde rápido; luz, aura e recolhimento mudam devagar. */
const TAU_DO_PARAMETRO: Record<keyof ParametrosDoOrbe, number> = {
    velocidade: 220, deformacao: 110, fluxo: 160, ondulacao: 70, energia: 110, brilho: 600, recolher: 800, aura: 380, abertura: 130, halo: 520,
};

export function misturarParametros(atual: ParametrosDoOrbe, alvo: ParametrosDoOrbe, dtMs: number): ParametrosDoOrbe {
    const dt = Math.min(Math.max(dtMs, 0), PASSO_MAXIMO_MS);
    const passo = (chave: keyof ParametrosDoOrbe) => atual[chave] + (alvo[chave] - atual[chave]) * (1 - Math.exp(-dt / TAU_DO_PARAMETRO[chave]));
    return {
        velocidade: passo('velocidade'),
        deformacao: passo('deformacao'),
        fluxo: passo('fluxo'),
        ondulacao: passo('ondulacao'),
        energia: passo('energia'),
        brilho: passo('brilho'),
        recolher: passo('recolher'),
        aura: passo('aura'),
        abertura: passo('abertura'),
        halo: passo('halo'),
    };
}
